/* ⚠️ GENERATED from app/services/aiContext.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/aiContext.ts — what the assistant is being shown, in words.
//
// ══ WHY THIS EXISTS ════════════════════════════════════════════════════════
// 🟥 REPLYING TO SOMETHING AND ASKING THE ASSISTANT ABOUT IT SENT ONLY THE
//    QUESTION. `@ai what does this say` was delivered to the model as the four
//    words "what does this say", with no trace of the document, photo, flight
//    card or voice note the user had quoted. The model answered the only way it
//    could — by asking to be shown the thing — and looked broken doing it, on a
//    screen where the quoted message was right there above the answer.
//
//    The reply was never missing from the MESSAGE: `replyTo` rides along and the
//    bubble draws it. It was missing from the PROMPT.
//
// 🟢 SO A MESSAGE BECOMES A SENTENCE. One function, both clients, every type —
//    and the same sentence whichever client asks, because a group conversation
//    can have a phone and a browser asking about the same quoted message and
//    they must not get different answers.
//
// 🟥 THIS DESCRIBES; IT DOES NOT OPEN. Media in this app is end-to-end
//    encrypted, and reading a document's CONTENTS means decrypting it and
//    handing the plaintext to a third-party model — a decision about what this
//    product promises, not a detail of a helper. So a PDF is reported as a PDF
//    with its name and size, and the model is told plainly that it has not been
//    given the contents. An assistant that says "I can see you attached
//    Q3-report.pdf but I can't read inside it" is honest; one that says "please
//    paste the document" when a document is right there is not.
//
// Pure: no react-native import, no firebase, no I/O. The suite executes it.

/** A size a person would say out loud. */
export function humanSize(bytes) {
  const n = Number(bytes) || 0;
  if (n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** mm:ss out of seconds, for a voice note or a clip. */
export function humanDuration(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  if (!s) return '';
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * 🟥 TRUE WHEN THE MODEL IS BEING TOLD *ABOUT* SOMETHING IT CANNOT READ.
 *    The caller uses this to add one honest line to the prompt rather than
 *    letting the model guess why the user's question cannot be answered.
 */
export function aiQuotedIsOpaque(msg) {
  const t = String(msg?.type || '');
  return t === 'document' || t === 'image' || t === 'images' || t === 'video' || t === 'audio';
}

/**
 * One message, described for the model.
 *
 * 🟢 THE SHAPE IS `[Kind: detail]`, because that is what the unread-summary
 *    prompt already uses and the model already reads well. Consistency across
 *    two prompts is worth more than a nicer sentence in one of them.
 */
export function describeMessageForAi(msg) {
  if (!msg) return '';
  const m = msg;
  const t = String(m.type || 'text');
  const cap = String(m.caption || '').trim();
  const withCap = s => cap ? `${s} with the caption "${cap}"` : s;
  if (m.systemType || t === 'system') return `[System update: ${String(m.text || 'update').trim()}]`;
  if (t === 'security') return '[Security notice: a new login was detected]';
  switch (t) {
    case 'text':
      return String(m.text || '').trim();
    case 'image':
      /* 🟢 a sticker is a `type: 'image'` wearing a flag — saying "photo" would
         be wrong in a way the user would notice immediately */
      return m.isSticker ? '[Sticker]' : withCap('[Photo]');
    case 'images':
      {
        const n = Array.isArray(m.images) ? m.images.length : 0;
        return withCap(n > 0 ? `[Album of ${n} photos or videos]` : '[Album of photos]');
      }
    case 'video':
      {
        const d = humanDuration(m.duration);
        return withCap(d ? `[Video, ${d}]` : '[Video]');
      }
    case 'audio':
      {
        const d = humanDuration(m.duration);
        /* 🟥 NOTHING IN THIS APP TRANSCRIBES A VOICE NOTE, so the model is told a
           voice note exists and how long it is — never what it says. */
        return d ? `[Voice message, ${d} long]` : '[Voice message]';
      }
    case 'document':
      {
        /* 🟥 THIS READ `m.fileName || m.name`, NEITHER OF WHICH THIS APP WRITES.
           The field is `documentName`, so every document in the unread summary
           rendered as the literal "[Document: file]" — a placeholder shipped as
           a description. */
        const name = String(m.documentName || '').trim();
        const size = humanSize(m.documentSize);
        const bits = [name || 'file', size].filter(Boolean).join(', ');
        return `[Document: ${bits}]`;
      }
    case 'location':
      {
        const named = String(m.locationName || '').trim();
        if (named) return `[Location: ${named}]`;
        const lat = Number(m.latitude),
          lng = Number(m.longitude);
        return Number.isFinite(lat) && Number.isFinite(lng) && (lat || lng) ? `[Location: ${lat.toFixed(4)}, ${lng.toFixed(4)}]` : '[Location]';
      }
    case 'contact':
      {
        const n = String(m.contactName || '').trim();
        return n ? `[Shared contact: ${n}]` : '[Shared contact]';
      }
    case 'poll':
      {
        const q = String(m.question || '').trim();
        const opts = Array.isArray(m.options) ? m.options.map(o => String(o?.text ?? o ?? '').trim()).filter(Boolean) : [];
        const tail = opts.length ? ` Options: ${opts.join(', ')}.` : '';
        return q ? `[Poll: "${q}".${tail}]` : `[Poll.${tail}]`;
      }
    case 'flight':
      {
        /* 🟥 AND THIS READ `m.flightNumber`, WHICH IS ALSO NOT A FIELD. The card's
           data is nested under `flightData`, so every flight described itself as
           the bare words "[Flight card]" — the one message type where the useful
           detail is the entire point of the card. */
        const f = m.flightData || {};
        const num = String(f.flightNumber || '').trim();
        const from = String(f.origin?.code || f.origin?.city || '').trim();
        const to = String(f.destination?.code || f.destination?.city || '').trim();
        const status = String(f.status || '').trim();
        const route = from && to ? `${from} → ${to}` : '';
        const bits = [num, route, status].filter(Boolean).join(', ');
        return bits ? `[Flight card: ${bits}]` : '[Flight card]';
      }
    case 'shared_post':
      {
        const who = String(m.postOwnerName || '').trim();
        const body = String(m.postContent || '').trim();
        if (m.linkUrl) {
          const title = String(m.linkTitle || '').trim();
          const dom = String(m.linkDomain || '').trim();
          return `[Shared a link${title ? `: "${title}"` : ''}${dom ? ` from ${dom}` : ''}]`;
        }
        const head = who ? `[Shared a post by ${who}` : '[Shared a post';
        return body ? `${head}: "${body}"]` : `${head}]`;
      }
    default:
      return String(m.text || '').trim() || '[Message]';
  }
}

/**
 * The block that goes in front of the user's question.
 *
 * 🟢 NAMED, BECAUSE "THIS" NEEDS AN ANTECEDENT. The user's question is almost
 *    always "what does this say" or "summarise this" — pronouns with nothing to
 *    point at. Putting the quoted message above the question, labelled, is what
 *    turns that into an answerable request.
 */
export function buildAiReplyContext(quoted, opts = {}) {
  if (!quoted) return '';
  const body = describeMessageForAi(quoted);
  if (!body) return '';
  const who = opts.isMine ? 'themselves' : String(opts.senderName || quoted.senderName || '').trim() || 'someone else';
  const lines = [`The user is replying to this message (sent by ${who}):`, body];
  if (aiQuotedIsOpaque(quoted)) {
    /* 🟥 WITHOUT THIS LINE THE MODEL INVENTS A REASON. Told about a document it
       cannot read and asked what it says, it will either hallucinate contents
       or ask the user to paste something they have already attached. Told the
       truth, it says what it can see and what it cannot — which is the answer
       the user can actually act on. */
    lines.push('You have been given only a description of that attachment, not its contents — ' + 'files in this app are end-to-end encrypted. Say what you can tell from the ' + 'description, and if the question needs what is inside the file, say plainly ' + 'that you cannot open it rather than asking them to paste it.');
  }
  return lines.join('\n');
}

/**
 * 🟢 WHERE THE ANSWER IS ALLOWED TO GO. A question asked in a room full of
 *    people has two reasonable answers and only the asker knows which: one
 *    everybody reads, and one only they do.
 */

/** Does this chat need the question asked at all? */
export function aiNeedsAudienceChoice(opts) {
  /* 🟥 THE ASSISTANT'S OWN CHAT HAS NOBODY TO HIDE FROM, and asking there is a
     tap between the user and every single answer. A one-to-one chat still has
     one other person in it, so the question is real. */
  if (opts.isAssistantChat) return false;
  if (opts.isGroup) return true;
  return (Number(opts.participantCount) || 2) > 1;
}
